import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import Errors, { getString } from "../../../components/errors/Errors";
import Listview from "../../../components/listview/Listview";
import { DefaultPagination, getPagination, Pagination } from "../../../components/listview/Pagination";
import useAbortController from "../../../hooks/useAbortController";
import useStatusBarState from "../../../hooks/useStatusBar";
import { Api } from "../../../listsSettings/api";
import { ErrorCode, IAction, IListviewField, SearchResult, UserPermissions } from "../../../services/types";
import { getCurrentUrlAsSource, getSelectedItems } from "../../../services/utils";
import { getUserPermissions } from "../../../services/webapi";
import strings from "../../../strings";
import Add from "../../../svg/Add.svg?react";
import Bin from "../../../svg/Bin.svg?react";
import * as Fields from "./Fields";
import { UserFunctionaryPermissionFull } from "./types";
import { newForm } from "./urls";
import {
  deleteUserFunctionaryPermissions,
  getUserFunctionaryPermissionFilters,
  getUserFunctionaryPermissions,
} from "./webapi";

const AllItems: FC = () => {
  const [abortController, resetAbortController] = useAbortController();
  const listviewId = "user-conventions-permissions-all-items";
  const fields: IListviewField<UserFunctionaryPermissionFull>[] = [Fields.userName, Fields.userKey];

  const statusBar = useStatusBarState();
  const [apiPermissions, setUserFunctionaryPermissions] = useState<SearchResult<UserFunctionaryPermissionFull> | null>(
    null,
  );
  const [errorCode, setErrorCode] = useState<ErrorCode>();

  const [permissions, setPermissions] = useState<UserPermissions>();
  const navigateTo = useNavigate();

  const pagination = getPagination(listviewId) || {
    ...DefaultPagination,
    orderBy: Fields.userName.fieldName,
  };

  useEffect(() => {
    loadUserFunctionaryPermissions(pagination);

    return () => {
      abortController && abortController.abort();
    };
  }, []);

  const loadUserFunctionaryPermissions = async (pagination: Pagination) => {
    const abortController = resetAbortController();
    const permissions = await getUserPermissions(
      Api.Lists.UserFunctionaryPermissions.InternalName,
      Api.InternalName,
      abortController.signal,
    );
    if (!abortController.signal.aborted) {
      setPermissions(permissions);
      const canRead = permissions.schemaPermission && permissions.listPermissions.includes("read-all");
      !canRead && setErrorCode(403);
      if (canRead) {
        const result = await getUserFunctionaryPermissions(pagination, abortController.signal);
        if (!abortController.signal.aborted) {
          result.data && setUserFunctionaryPermissions(result.data);
          result.error && setErrorCode(result.error.code);
        }
      }
    }
  };

  const newAction: IAction = {
    title: strings.new,
    icon: Add,

    onClick: () => navigateTo(`${newForm}?${getCurrentUrlAsSource()}`),
    disabled: !permissions || !permissions.listPermissions.includes("create"),
  };

  const deleteAction: IAction = {
    title: strings.delete,
    icon: Bin,
    onClick: async () => {
      const selectedItems = getSelectedItems(apiPermissions);
      if (selectedItems.length > 0 && confirm(strings.confirmDeleteItems)) {
        const abortController = resetAbortController();
        const result = await deleteUserFunctionaryPermissions(
          selectedItems.map((x) => x.user.id),
          abortController.signal,
        );

        if (result.error) {
          statusBar.addError(getString(result.error));
        } else if (result.data) {
          const s = selectedItems.filter((x) => x.user.id && result.data?.[x.user.id]);

          if (s.length != selectedItems.length) {
            statusBar.addWarning(strings.notAllElementsDeleted);
          }
          if (s.length > 0) {
            await loadUserFunctionaryPermissions(pagination);
          }
        }
      }
    },
    disabled: getSelectedItems(apiPermissions).length === 0 || !permissions?.listPermissions.includes("delete-all"),
  };

  const actions = [newAction, deleteAction];

  return (
    <>
      {permissions &&
        (!errorCode ? (
          <Listview
            id={listviewId}
            actions={actions}
            viewProperties={{
              schema: Api.InternalName,
              list: Api.Lists.UserFunctionaryPermissions.InternalName,
              name: "all_items",
              setListAndPage: true,
            }}
            fields={fields}
            allowSelectRows
            reload={loadUserFunctionaryPermissions}
            searchResult={apiPermissions}
            getFilters={getUserFunctionaryPermissionFilters}
            pagination={pagination}
            updateItems={(items) =>
              apiPermissions &&
              setUserFunctionaryPermissions({
                ...apiPermissions,
                items: items,
              })
            }
          />
        ) : (
          errorCode && <Errors errorCode={errorCode} />
        ))}
    </>
  );
};

export default AllItems;
